import React from 'react';
import styled from 'styled-components';

import { IconButton } from '@/ui/Button';
import { DocumentVideoUploadButton } from '@/component/FileUpload/DocumentVideoUploadButton';
import { getShortenedText } from '@/utils/text';
import { FileUploaderError } from '@/component/FileUpload/types';

const MAX_FILENAME_LENGTH = 40;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const UploadedContainerStyled = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
`;

const HeaderStyled = styled.div`
  display: flex;
  height: 66px;
  padding: 0 16px;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  background: ${({ theme }) => theme.colors.neutral.background};
  box-sizing: border-box;
`;

const DividerStyled = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.neutral.outline1};
`;

const FileCardContainerStyled = styled.div`
  display: flex;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
`;

const ContentStyled = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
`;

const InformerStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  width: 56px;
  background: ${({ theme }) => theme.colors.secondary.container};
  border-radius: 8px;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const InformerTextStyled = styled.div`
  color: ${({ theme }) => theme.colors.tertiary.onContainer};
  ${(p) => p.theme.typography.overline};
  white-space: nowrap;
  overflow: hidden;
  max-width: 90%;
  text-overflow: ellipsis;
  box-sizing: border-box;
`;

const TitleContainerStyled = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

const FileNameStyled = styled.div`
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  ${(p) => p.theme.typography.title4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const FileExtensionStyled = styled.div`
  color: ${({ theme }) => theme.tones.neutral[60]};
  ${(p) => p.theme.typography.title4};
  white-space: nowrap;
  flex-shrink: 0;
`;

const FileCardActionStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const ErrorStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.error.main};
`;

const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
};

const getFileName = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split('/').pop() || 'file';
    return fileName;
  } catch {
    return 'file';
  }
};

const splitFileName = (fileName: string): { name: string; extension: string } => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return { name: getShortenedText(fileName, MAX_FILENAME_LENGTH), extension: '' };
  }
  const nameWithoutExt = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex);
  return {
    name: getShortenedText(nameWithoutExt, MAX_FILENAME_LENGTH),
    extension,
  };
};

type FeedbackFileUploadUploadedProps = {
  uploadedFileUrl: string;
  originalFileName: string | null;
  fileType: 'image' | 'video';
  onReplace: (file: File) => void;
  imageUploaderError?: FileUploaderError | null;
  videoUploaderError?: FileUploaderError | null;
};

export const FeedbackFileUploadUploaded = ({
  uploadedFileUrl,
  originalFileName,
  fileType,
  onReplace,
  imageUploaderError,
  videoUploaderError,
}: FeedbackFileUploadUploadedProps): JSX.Element => {
  const fileName = originalFileName || getFileName(uploadedFileUrl);
  const fileExtension = getFileExtension(fileName);
  const { name: fileTitle, extension: fileExt } = splitFileName(fileName);

  return (
    <ContainerStyled>
      <UploadedContainerStyled>
        <HeaderStyled>
          <DocumentVideoUploadButton
            icon={['fas', 'repeat']}
            variant="ghost"
            size="large"
            uploadFile={onReplace}
            fileType={fileType}
            accept="image/*,video/*"
            type="button"
          >
            Replace
          </DocumentVideoUploadButton>
        </HeaderStyled>
        <DividerStyled />
        <FileCardContainerStyled>
          <ContentStyled>
            <InformerStyled>
              <InformerTextStyled>{fileExtension}</InformerTextStyled>
            </InformerStyled>
            <TitleContainerStyled>
              <FileNameStyled>{fileTitle}</FileNameStyled>
              <FileExtensionStyled>{fileExt}</FileExtensionStyled>
            </TitleContainerStyled>
          </ContentStyled>
          <FileCardActionStyled>
            {fileType === 'image' && <IconButton href={uploadedFileUrl} target="_blank" icon={['far', 'arrow-down-to-line']} />}
          </FileCardActionStyled>
        </FileCardContainerStyled>
      </UploadedContainerStyled>
      {(imageUploaderError || videoUploaderError) && (
        <ErrorStyled>{imageUploaderError?.message || videoUploaderError?.message}</ErrorStyled>
      )}
    </ContainerStyled>
  );
};
